import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MUSCLE_GROUPS } from './strengthFormat';

const KIND_ICON = { Weighted: '🏋️', Bodyweight: '🤸', Timed: '⏱' };

function ExercisePicker({ t, catalog, excludeKeys = [], onPick, onClose }) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const searchRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus once on open; the parent re-renders every second for its clock.
  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    return catalog
      .filter(e => !excludeKeys.includes(e.key))
      .filter(e => !group || e.group === group)
      .filter(e => !term || e.name.toLowerCase().includes(term) || e.key.toLowerCase().replace(/_/g, ' ').includes(term))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog, excludeKeys, group, search]);

  return (
    <div className="st-modal-backdrop" onClick={onClose}>
      <div
        className="st-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('strength.picker.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="st-modal-head">
          <h3>{t('strength.picker.title')}</h3>
          <button type="button" className="st-icon-btn" onClick={onClose} aria-label={t('strength.common.close')}>✕</button>
        </div>
        <input
          ref={searchRef}
          className="st-input"
          type="search"
          placeholder={t('strength.picker.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
        />
        <div className="st-chips" role="group" aria-label={t('strength.picker.groups')}>
          <button type="button" className={`st-chip ${group === '' ? 'active' : ''}`} onClick={() => setGroup('')}>
            {t('strength.picker.all')}
          </button>
          {MUSCLE_GROUPS.map(g => (
            <button type="button" key={g} className={`st-chip ${group === g ? 'active' : ''}`} onClick={() => setGroup(g)}>
              {t(`strength.groups.${g}`)}
            </button>
          ))}
        </div>
        <ul className="st-picker-list">
          {results.map(e => (
            <li key={e.key}>
              <button type="button" onClick={() => onPick(e.key)}>
                <span aria-hidden="true">{KIND_ICON[e.kind]}</span>
                <span className="st-picker-name">{e.name}</span>
                <span className="st-muted">{t(`strength.groups.${e.group}`)}</span>
              </button>
            </li>
          ))}
          {results.length === 0 && <li className="st-empty">{t('strength.picker.none')}</li>}
        </ul>
      </div>
    </div>
  );
}

export default ExercisePicker;
